import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { RootStyle } from '@ygtechnologies/rubus-ui';
import { OptionConfiguratorMainStyle } from './OptionConfiguratorMainStyle';
import { StyleDetails } from './StyleDetails';
import { Room } from './Room';
import { BottomPanel } from './BottomPanel';
import { OptionsStartCustomizationHero } from './OptionsStartCustomizationHero/OptionsStartCustomizationHero';
import { ImmersiveConfigurator } from './ImmersiveConfigurator/ImmersiveConfigurator';
import { OptionConfiguratorProvider } from './OptionConfiguratorContext';
import { ApiMutationProvider } from './ApiMutationProvider';
import { OptionSigning } from './OptionSigning/OptionSigning';
import { OptionSigningPre } from './OptionSigningPre/OptionSigningPre';
import BuyerPageTitle from '@/buyer/components/BuyerPageTitle/BuyerPageTitle';
import { OptionConfiguratorHomePage } from './OptionConfiguratorHomePage/HomePage';
import {
    OptionsPage as OptionsPageLegacy,
    BuyerOptionSubmit,
    OptionsScopeProvider as LegacyOptionsScopeProvider,
    OptionsFilterProvider,
} from '@/components/OptionConfigurator/legacyNext';
import { IState } from '@/state/IState';
import { getBuyerProjectInfo } from '@/services/buyer-dashboard';
import { useIsImpersonating } from '@/utils/hooks';
import styles from './OptionConfigurator.module.scss';

export interface ConfiguratorProps {
    residentialUnitId: number;
}

export const BuyerOptionConfigurator: React.FC<ConfiguratorProps> = ({ residentialUnitId }) => {
    const match = useRouteMatch();
    const projectData = useSelector((state: IState) => state.buyer.projectData);
    const permissions = useSelector((state: IState) => state.buyer.permissions);
    // A seller impersonating a buyer may view but not change selections (mirrors the signing block).
    const isImpersonating = useIsImpersonating();
    // Strip any trailing slash — the parent route is registered as
    // `${...}/configurator/` (with trailing slash), and a double-slash in a
    // child route pattern would prevent it matching the URL.
    const basePath = (match?.path ?? '').replace(/\/$/, '');
    const baseUrl = (match?.url ?? '').replace(/\/$/, '');

    const scope = useMemo(() => {
        if (!projectData || !permissions) return null;
        return {
            residentialUnitId: permissions.residentialUnitId,
            projectData,
            mode: 'buyer' as const,
            readOnly: isImpersonating,
            showSubmit: true,
        };
    }, [projectData, permissions, isImpersonating]);

    if (!projectData || !permissions || !scope) {
        return <LoadingIndicator />;
    }

    if (!projectData.useLegacyOptionsView) {
        return (
            <Switch>
                {/* Full-screen immersive configurator (the "options 2.0" two-tier experience),
                    reached from the "Start customization" hero. Renders without the buyer page title. */}
                <Route path={`${basePath}/configurator`}>
                    <ImmersiveConfigurator residentialUnitId={residentialUnitId} />
                </Route>
                <Route>
                    <>
                        <BuyerPageTitle />
                        <RootStyle
                            value={{
                                '--configurator-panel-top-position': 'var(--cm-header-height)',
                            }}
                        />
                        <div className="fit-height">
                            <OptionConfigurator residentialUnitId={residentialUnitId} />
                        </div>
                    </>
                </Route>
            </Switch>
        );
    }

    return (
        <>
            <BuyerPageTitle />
            <RootStyle
                value={{
                    '--configurator-panel-top-position': 'var(--cm-header-height)',
                }}
            />
            {/* No `fit-height`: legacy scrolls at the page level so the sticky header/sidebar/headers
                have a tall containing block. `fit-height` caps it to one viewport and breaks them. */}
            <div>
                {/* Legacy now consumes the shared data/mutation contexts (like v2). It shows every
                    deadline on one page, so it opts out of URL-based deadline tracking. */}
                <OptionConfiguratorProvider
                    residentialUnitId={residentialUnitId}
                    basePath={baseUrl}
                    baseUrl={baseUrl}
                    manageDeadlineInUrl={false}>
                    <ApiMutationProvider>
                        <LegacyOptionsScopeProvider value={scope}>
                            <OptionsFilterProvider>
                                <Switch>
                                    <Route
                                        path={`${basePath}/submit/:projectDeadlineId`}
                                        render={(props) => (
                                            <BuyerOptionSubmit {...props} backToOptionsUrl={baseUrl} />
                                        )}
                                    />
                                    <Route component={OptionsPageLegacy} />
                                </Switch>
                            </OptionsFilterProvider>
                        </LegacyOptionsScopeProvider>
                    </ApiMutationProvider>
                </OptionConfiguratorProvider>
            </div>
        </>
    );
};

export const SellerOptionConfigurator: React.FC<ConfiguratorProps> = ({ residentialUnitId }) => {
    const activeProject = useSelector((state: IState) => state.global.activeProject);
    const useLegacy = activeProject?.useLegacyOptionsView ?? false;
    const location = useLocation();

    const { data: projectData, isLoading } = useQuery({
        queryKey: ['legacyOptionsSellerProjectData', residentialUnitId],
        queryFn: () => getBuyerProjectInfo(residentialUnitId),
        enabled: useLegacy && !!residentialUnitId,
    });

    // Resolve the options base URL by slicing the path at the `options` segment, so the
    // sign-and-send route works under the seller tab route (`.../:tab/:id?`), whose param
    // capture would otherwise mangle a `match.url`-derived base. Mirrors `OptionConfigurator`.
    const baseUrl = useMemo(() => {
        const parts = location.pathname.split('/');
        const idx = parts.findIndex((p) => p === 'options');
        return idx >= 0 ? parts.slice(0, idx + 1).join('/') : location.pathname.replace(/\/$/, '');
    }, [location.pathname]);

    if (!useLegacy) {
        return (
            <Switch>
                {/* Full-screen immersive configurator, reached from the "Start customization" hero.
                    Mirrors the buyer flow (see BuyerOptionConfigurator). */}
                <Route path={`${baseUrl}/configurator`}>
                    <ImmersiveConfigurator residentialUnitId={residentialUnitId} />
                </Route>
                <Route>
                    <>
                        <RootStyle
                            value={{
                                '--configurator-panel-top-position': 'calc( var(--cm-header-height) + 110px)',
                            }}
                        />
                        <div className={styles.sellerConfigurator}>
                            <OptionConfigurator residentialUnitId={residentialUnitId} />
                        </div>
                    </>
                </Route>
            </Switch>
        );
    }

    if (isLoading || !projectData) {
        return <LoadingIndicator />;
    }

    const scope = {
        residentialUnitId,
        projectData,
        mode: 'seller' as const,
        // Seller editing on the seller tab is not impersonation; selections remain changeable.
        readOnly: false,
        showSubmit: true,
    };

    return (
        <>
            <RootStyle
                value={{
                    '--configurator-panel-top-position': 'calc( var(--cm-header-height) + 110px)',
                }}
            />
            <OptionConfiguratorProvider
                residentialUnitId={residentialUnitId}
                basePath={baseUrl}
                baseUrl={baseUrl}
                manageDeadlineInUrl={false}>
                <ApiMutationProvider>
                    <LegacyOptionsScopeProvider value={scope}>
                        <OptionsFilterProvider>
                            <Switch>
                                <Route
                                    path={`${baseUrl}/submit/:projectDeadlineId`}
                                    render={(props) => <BuyerOptionSubmit {...props} backToOptionsUrl={baseUrl} />}
                                />
                                <Route component={OptionsPageLegacy} />
                            </Switch>
                        </OptionsFilterProvider>
                    </LegacyOptionsScopeProvider>
                </ApiMutationProvider>
            </OptionConfiguratorProvider>
        </>
    );
};

const ConfiguratorRoutes: React.FC<{ basePath: string }> = ({ basePath }) => {
    return (
        <div className="fit-height">
            <Switch>
                <Route
                    path={`${basePath}/deadline/:deadlineId/style-group/:styleGroupId/style/:styleId`}
                    component={StyleDetails}
                />
                <Route
                    path={`${basePath}/deadline/:deadlineId/style-group/:styleGroupId`}
                    component={OptionConfiguratorMainStyle}
                />
                <Route exact path={`${basePath}/deadline/:deadlineId/room/:roomId`} component={Room} />
                <Route
                    path={`${basePath}/deadline/:deadlineId/room/:roomId/style-group/:styleGroupId/style/:styleId`}
                    component={StyleDetails}
                />
                <Route path={`${basePath}/deadline/:deadlineId/sign-pre`} component={OptionSigningPre} />
                <Route path={`${basePath}/deadline/:deadlineId/sign/:clientKey`} component={OptionSigning} />
                <Route path={basePath} component={OptionConfiguratorHomePage} />
            </Switch>
        </div>
    );
};

export const OptionConfigurator: React.FC<ConfiguratorProps> = ({ residentialUnitId }) => {
    const match = useRouteMatch();
    const location = useLocation();
    const baseUrl = React.useMemo(() => {
        const path = location.pathname;
        const parts = path.split('/');
        const idx = parts.findIndex((p) => p === 'options');
        if (idx >= 0) {
            return parts.slice(0, idx + 1).join('/') || '/';
        }
        const u = match?.url ?? '';
        return u.endsWith('/options') ? u : `${u}/options`;
    }, [location.pathname, match?.url]);
    return (
        <OptionConfiguratorProvider residentialUnitId={residentialUnitId} basePath={baseUrl} baseUrl={baseUrl}>
            <ApiMutationProvider>
                {/* The options landing is the CTA hero + the deadline overview (with its sign-and-send
                    action). Room/style navigation lives in the full-screen immersive configurator,
                    reached via the hero — so no inline tab bar here. */}
                <OptionsStartCustomizationHero />
                <ConfiguratorRoutes basePath={baseUrl} />
                <BottomPanel />
            </ApiMutationProvider>
        </OptionConfiguratorProvider>
    );
};
